import { useState } from "react";
import { Box, Typography } from "@mui/material";
import { muiTheme } from "@/styles/theme";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import ResponsiveEditDialog from "@/modules/common/components/modals/responsive-edit-dialog";
import { getQuarter } from "date-fns";
import TargetsConfigurationsTable from "@/modules/targets/components/target-configurations/targets-configurations-table";
import { Eye } from "lucide-react";
import ResponsiveTabs from "@/modules/common/components/ui/responsive-tabs";
import { TargetsTemplateProvider } from "@/modules/targets/context/targets-template.context";

const quartersData: {
  value: number;
  label: string;
}[] = [
  { value: 1, label: "Q1" },
  { value: 2, label: "Q2" },
  { value: 3, label: "Q3" },
  { value: 4, label: "Q4" },
];

interface ViewQuartelyTargetProps {
  quarter: 1 | 2 | 3 | 4;
  year: number;
  handleChange: (quarter: 1 | 2 | 3 | 4) => void;
}

const ViewQuarterlyTarget: React.FC<ViewQuartelyTargetProps> = ({
  quarter,
  year,
  handleChange,
}) => {
  const [open, setOpen] = useState(false);

  const quarters = quartersData.filter((q) => {
    if (year === new Date().getFullYear()) {
      return q.value <= getQuarter(new Date());
    }
    return true;
  });

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
  };
  const { isMobile } = useScreenSize();

  const renderContent = () => {
    return (
      <ResponsiveEditDialog
        open={open}
        setOpen={handleClose}
        handleCloseModal={handleClose}
        header={`${new Date().getFullYear()} Q${getQuarter(
          new Date()
        )}, ${new Date().toLocaleString("default", { month: "long" })}`}
        hideModalFooter
        modalFooter={false}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: isMobile ? "100%" : "800px",
          }}
        >
          <ResponsiveTabs
            tabs={quarters}
            selectedTab={quarter}
            handleChange={(value) => {
              if (value >= 1 && value <= 4) {
                handleChange(value as 1 | 2 | 3 | 4);
              }
            }}
          />
          <TargetsTemplateProvider>
            <TargetsConfigurationsTable
              quarter={quarter}
              year={year}
              viewOnly
            />
          </TargetsTemplateProvider>
        </Box>
      </ResponsiveEditDialog>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 1,
          padding: "10px 16px",
          alignItems: "center",
          justifyContent: "center",
          textWrap: "nowrap",
          width: "100%",
          height: "100%",
          cursor: "pointer",
          ...muiTheme.typography.action_button,
          backgroundColor:
            muiTheme.colorSchemes.light.palette.main.actionDisabled,
          borderRadius: "8px",
          ":hover": {
            backgroundColor: muiTheme.colorSchemes.light.palette.main.grey,
          },
          opacity: 1,
        }}
        onClick={handleOpen}
      >
        <Typography
          sx={{
            color: "#015C9A",
          }}
          variant="action_button"
        >
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Eye size={24} />
            <span>View Quarter Target</span>
          </Box>
        </Typography>
      </Box>
      {renderContent()}
    </Box>
  );
};

export default ViewQuarterlyTarget;
